import * as React from "react";
import flags from "react-phone-number-input/flags";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AU_COUNTRY } from "@/components/ui/phoneInput.constants";
import {
  formatPhoneNumber,
  getFormattedPhoneInputCursorPosition,
  normalizeAuFlexiblePhoneInput,
} from "@/components/ui/phoneInput.utils";
import { cn } from "@/utils/core/generalUtils";

type AuFlexiblePhoneInputProps = {
  className?: string;
  disabled?: boolean;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  onChange: (value: string) => void;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  placeholder?: string;
  readOnly?: boolean;
  value?: string;
};

const AuFlag = flags[AU_COUNTRY];

const AuFlexiblePhoneInput = React.forwardRef<
  HTMLInputElement,
  AuFlexiblePhoneInputProps
>((props, ref) => {
  const {
    className,
    disabled,
    onBlur,
    onChange,
    onFocus,
    placeholder,
    readOnly,
    value,
  } = props;

  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const pendingCursorPositionRef = React.useRef<number | null>(null);

  const restorePendingCursorPosition = React.useCallback(() => {
    const pendingCursorPosition = pendingCursorPositionRef.current;
    const inputElement = inputRef.current;

    if (pendingCursorPosition === null || !inputElement) {
      return;
    }

    inputElement.setSelectionRange(
      pendingCursorPosition,
      pendingCursorPosition,
    );
    pendingCursorPositionRef.current = null;
  }, []);

  const handleInputRef = React.useCallback(
    (inputElement: HTMLInputElement | null) => {
      inputRef.current = inputElement;

      if (typeof ref === "function") {
        ref(inputElement);
        return;
      }

      if (ref) {
        ref.current = inputElement;
      }
    },
    [ref],
  );

  const handleChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const cursorPosition = event.target.selectionStart;

      if (cursorPosition !== null) {
        pendingCursorPositionRef.current = getFormattedPhoneInputCursorPosition(
          event.target.value,
          cursorPosition,
        );
      }

      onChange(normalizeAuFlexiblePhoneInput(event.target.value));
      window.requestAnimationFrame(restorePendingCursorPosition);
    },
    [onChange, restorePendingCursorPosition],
  );

  React.useLayoutEffect(() => {
    restorePendingCursorPosition();
  });

  return (
    <div className={cn("flex", className)}>
      <Button
        type='button'
        variant='outline'
        className='flex gap-1 rounded-e-none rounded-s-lg pr-1 pl-3 shadow-sm'
        disabled
      >
        <span className='flex w-6 h-4 overflow-hidden rounded-sm bg-foreground/20'>
          {AuFlag && <AuFlag title={AU_COUNTRY} />}
        </span>
      </Button>
      <Input
        className='rounded-s-none rounded-e-lg shadow-sm'
        disabled={disabled}
        onBlur={onBlur}
        onChange={handleChange}
        onFocus={onFocus}
        placeholder={placeholder}
        readOnly={readOnly}
        ref={handleInputRef}
        type='tel'
        value={formatPhoneNumber(value)}
      />
    </div>
  );
});
AuFlexiblePhoneInput.displayName = "AuFlexiblePhoneInput";

export default AuFlexiblePhoneInput;
